import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { ReactElement } from 'react';
import { Alert } from '../../modals/Alert';
import { AddDrawButton } from './buttons/AddDrawButton';
import { InspectionButton } from './buttons/InspectionButton';
import { ReceiptButton } from './buttons/ReceiptButton';
import { DrawApi, IDrawBudget } from '@apply/api/src/api/DrawApi';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DrawBudgetCategory } from './functions';

interface IDrawButtonGroup {
    drawBudget: IDrawBudget | undefined;
    set_drawBudget: Dispatch<SetStateAction<IDrawBudget | undefined>>;
    applicationInsights: ApplicationInsights;
    isAdmin: boolean;
    isEquityMet: boolean;
    isInspectionsEnabled: boolean;
    borrowerName: string;
    mobileScreen: boolean;
    categories: DrawBudgetCategory[];
}

export const DrawButtonGroup = ({
    drawBudget,
    set_drawBudget,
    applicationInsights,
    isAdmin,
    isEquityMet,
    isInspectionsEnabled,
    borrowerName,
    mobileScreen,
    categories,
}: IDrawButtonGroup): ReactElement => {
    const drawApi = useMemo(() => new DrawApi(applicationInsights), [applicationInsights]);

    const [isBudgetAlertOpen, set_isBudgetAlertOpen] = useState<boolean>(false);

    return (
        <>
            <div className='draw-button-group'>
                {!mobileScreen && (
                    <AddDrawButton
                        drawBudget={drawBudget}
                        set_drawBudget={set_drawBudget}
                        applicationInsights={applicationInsights}
                        isAdmin={isAdmin}
                        isApproved={drawBudget?.isApproved ?? false}
                        isFunded={drawBudget?.isFunded ?? false}
                        isEquityMet={isEquityMet}
                        isBudgetMaxed={drawBudget?.isBudgetMaxed ?? false}
                        bankAccountLast4Digits={drawBudget?.bankAccountLast4Digits}
                        borrowerName={borrowerName}
                    />
                )}
                {isAdmin && (
                    <button
                        id='budgetPdfButton'
                        type='button'
                        className='button primary'
                        onClick={() => {
                            if (!drawBudget) return;
                            drawApi.emailBudgetPdf(drawBudget.id);
                            set_isBudgetAlertOpen(true);
                        }}
                    >
                        Budget PDF
                    </button>
                )}
                {isAdmin && isInspectionsEnabled && (
                    <InspectionButton
                        drawBudget={drawBudget}
                        set_drawBudget={set_drawBudget}
                    />
                )}
                <ReceiptButton
                    categories={categories}
                    applicationInsights={applicationInsights}
                />
            </div>
            {isBudgetAlertOpen && (
                <Alert
                    onConfirm={() => set_isBudgetAlertOpen(false)}
                    title='Generating Report'
                    message='A PDF will be created and emailed to your address.'
                />
            )}
        </>
    );
};
